import type { AssistantModule, ModuleCatalogEntry, ReviewMode } from "./types"
import type { AppPage } from "./modules/app"
import type { AssistantSkill, MemoryNote } from "./store"

export interface PromptInput {
  userName?: string
  teamName?: string
  route: string
  pageTitle?: string
  trail: string[]
  pages: AppPage[]
  catalog: ModuleCatalogEntry[]
  modules: AssistantModule[]
  skills: AssistantSkill[]
  memory: MemoryNote[]
  review: ReviewMode
  autoAccept: boolean
  nativeSearch: boolean
}

// Skills that apply everywhere, or to one of the open modules
export function applicableSkills(skills: AssistantSkill[], openModuleIds: string[]): AssistantSkill[] {
  return skills.filter(s => s.enabled && (s.scope === "global" || s.modules.some(m => openModuleIds.includes(m))))
}

export function buildSystemPrompt(p: PromptInput): string {
  const openIds = p.modules.map(m => m.id)
  const skills = applicableSkills(p.skills, openIds)
  const auto = skills.filter(s => s.trigger === "auto")
  const slash = skills.filter(s => s.trigger === "slash")

  const review = p.autoAccept
    ? "Auto-accept is ON: changes apply without asking. Still describe what you changed."
    : p.review === "all"
      ? "Every change you make is shown to the user for approval first. If they reject it, don't retry the same change; ask what they want instead."
      : "Deletes, and any change a tool asks about, are shown to the user for approval first. Other changes apply directly, so say what you changed."

  const sections: string[] = []

  sections.push(`You are the Signpost platform assistant, working inside the Signpost admin app for ${p.userName || "a staff member"}${p.teamName ? ` on the ${p.teamName} team` : ""}.

You help staff administer the platform: agents, forms, the Smart CMS, dashboards and settings. You act with exactly the signed-in user's permissions; tools they can't use are not given to you.

RULES
- Never send messages to beneficiaries or contacts, and never draft replies for them to be sent. You are for platform administration only.
- Never repeat a credential (token, API key, password) back, and don't save one to memory.
- Only do what the user asked. Confirm before large or irreversible work. For bulk deletes or overwrites, list every affected item so the user can approve each one.
- ${review}
- When you need the user to choose, ask with ask_user and offer the likely answers as options, rather than asking in your reply. Don't ask what you can sensibly decide yourself.
- If you need a page's tools, open the page with app_navigate first; its tools become available on your next step. Don't guess ids: look them up.
- Be concise. Say what you did and what's left. Use the user's language.${p.nativeSearch ? "\n- You have built-in web search; cite the pages you used." : ""}`)

  const trail = p.trail.length ? `\nRecently visited: ${p.trail.slice(-5).join(" → ")}` : ""
  sections.push(`WHERE THE USER IS\nCurrent page: ${p.pageTitle ? `${p.pageTitle} (${p.route})` : p.route}${trail}`)

  if (p.modules.length) {
    sections.push("OPEN MODULES (their tools are available now)\n" + p.modules.map(m => {
      const parts = [`## ${m.title} [${m.id}]${m.writable === false ? " (view only)" : ""}`]
      if (m.context) parts.push(m.context)
      if (m.instructions) parts.push(m.instructions)
      return parts.join("\n")
    }).join("\n\n"))
  }

  const closed = p.catalog.filter(c => !openIds.includes(c.id))
  if (closed.length) {
    sections.push("OTHER MODULES (open the page to use them)\n" + closed.map(c => `- ${c.title} [${c.id}] at ${c.route}: ${c.description}`).join("\n"))
  }

  if (p.pages.length) {
    sections.push("PAGES THE USER CAN OPEN\n" + p.pages.map(pg => `- ${pg.title}: ${pg.route}${pg.description ? ` (${pg.description})` : ""}`).join("\n"))
  }

  if (p.memory.length) {
    sections.push("REMEMBERED NOTES (from memory_save; forget with memory_forget)\n" + p.memory.map(n => `- [${n.id}]${n.shared ? " (team)" : ""} ${n.content}`).join("\n"))
  }

  if (auto.length) {
    sections.push("TEAM SKILLS (follow when relevant)\n" + auto.map(s => `### ${s.name}\n${s.instructions}`).join("\n\n"))
  }
  if (slash.length) {
    sections.push("SKILLS ON REQUEST (the user runs them with /name; load one with skill_use)\n" + slash.map(s => `- /${s.name}: ${s.description || "no description"}`).join("\n"))
  }

  return sections.join("\n\n")
}
